import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import MDInput from "components/MDInput";

export default function LoyaltyPurchasesPage() {
  usePageTitle("Loyalty Purchases");

  const navigate = useNavigate();
  const { ref } = useParams();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [search, setSearch] = useState("");
  const [verified, setVerified] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedTerritories, setSelectedTerritories] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 25,
    total_records: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  const buildQueryString = (filters = {}, exportData = false) => {
    const params = new URLSearchParams();

    params.set("page", filters.page || 1);
    params.set("page_size", pagination.page_size);

    if (filters.search) {
      params.set("search", filters.search);
    }

    if (filters.verified !== "") {
      params.set("verified", filters.verified);
    }

    if (filters.start_date) {
      params.set("start_date", filters.start_date);
    }

    if (filters.end_date) {
      params.set("end_date", filters.end_date);
    }

    (filters.product || []).forEach((product) => {
      params.append("product", product);
    });

    (filters.territory || []).forEach((territory) => {
      params.append("territory", territory);
    });

    if (exportData) {
      params.set("export", "true");
      params.delete("page");
      params.delete("page_size");
    }

    return params.toString();
  };

  const fetchPurchases = useCallback(
    async (filters = {}) => {
      setLoading(true);

      try {
        const queryString = buildQueryString(filters);

        const res = await axios.get(
          `/portal/tm/loyalty/promotions/${ref}/purchases.json?${queryString}`
        );

        if (res.data.status === "success") {
          const data = res.data.data;
          const currency = res.data.currency;

          const rows = (data.purchases || []).map((row) => ({
            consumer: `${row.consumer.first_name} ${row.consumer.last_name}`,
            product: row.product.title,
            points: row.points,
            total: row.purchase_total_price
              ? `${currency}${(row.purchase_total_price / 100).toLocaleString("en", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : "---",
            verified: row.verified ? "Yes" : "No",
            created: row.created || "---",
            actions: (
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                iconOnly
                onClick={() => navigate(`/tm/loyalty/promotions/purchases/${row.reference}`)}
              >
                <Icon fontSize="small">east</Icon>
              </MDButton>
            ),
          }));

          setPurchases(rows);
          setProducts(data.products || []);
          setTerritories(data.territories || []);
          setPagination(
            data.pagination || {
              page: 1,
              page_size: 25,
              total_records: rows.length,
              total_pages: 1,
              has_next: false,
              has_previous: false,
            }
          );

          setLoaded(true);
        } else {
          setErrMsg(res.data.data.message || "Failed to load purchases.");
          setOpen(true);
        }
      } catch (error) {
        setErrMsg("Failed to load purchases.");
        setOpen(true);
      } finally {
        setLoading(false);
      }
    },
    [ref, navigate, pagination.page_size]
  );

  const getCurrentFilters = (page = 1) => ({
    search: search.trim(),
    verified,
    start_date: startDate,
    end_date: endDate,
    product: selectedProducts,
    territory: selectedTerritories,
    page,
  });

  useEffect(() => {
    fetchPurchases({
      search: "",
      verified: "",
      start_date: "",
      end_date: "",
      product: [],
      territory: [],
      page: 1,
    });
  }, [ref]);

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      setErrMsg("Start date cannot be later than end date.");
      setOpen(true);
      return;
    }

    fetchPurchases(getCurrentFilters(1));
  };

  const handleReset = () => {
    setSearch("");
    setVerified("");
    setSelectedProducts([]);
    setSelectedTerritories([]);
    setStartDate("");
    setEndDate("");

    fetchPurchases({
      search: "",
      verified: "",
      start_date: "",
      end_date: "",
      product: [],
      territory: [],
      page: 1,
    });
  };

  const handlePageChange = (page) => {
    fetchPurchases(getCurrentFilters(page));
  };

  const handlePageSizeChange = (event) => {
    const pageSize = Number(event.target.value);

    setPagination((previous) => ({
      ...previous,
      page_size: pageSize,
      page: 1,
    }));

    setLoading(true);

    const params = new URLSearchParams();
    params.set("page", "1");
    params.set("page_size", String(pageSize));

    const filters = getCurrentFilters(1);

    if (filters.search) params.set("search", filters.search);
    if (filters.verified !== "") params.set("verified", filters.verified);
    if (filters.start_date) params.set("start_date", filters.start_date);
    if (filters.end_date) params.set("end_date", filters.end_date);

    filters.product.forEach((product) => {
      params.append("product", product);
    });

    filters.territory.forEach((territory) => {
      params.append("territory", territory);
    });

    axios
      .get(`/portal/tm/loyalty/promotions/${ref}/purchases.json?${params.toString()}`)
      .then((res) => {
        if (res.data.status === "success") {
          const data = res.data.data;

          setPurchases(
            (data.purchases || []).map((row) => ({
              consumer: `${row.consumer.first_name} ${row.consumer.last_name}`,
              product: row.product.title,
              points: row.points,
              total: row.purchase_total_price ?? "---",
              verified: row.verified ? "Yes" : "No",
              created: row.created || "---",
              actions: (
                <MDButton
                  size="small"
                  variant="outlined"
                  color="dark"
                  iconOnly
                  onClick={() => navigate(`/tm/loyalty/purchases/${row.reference}`)}
                >
                  <Icon fontSize="small">visibility</Icon>
                </MDButton>
              ),
            }))
          );

          setProducts(data.products || []);
          setTerritories(data.territories || []);
          setPagination(data.pagination);
          setLoaded(true);
        } else {
          setErrMsg(res.data.data.message || "Failed to load purchases.");
          setOpen(true);
        }
      })
      .catch(() => {
        setErrMsg("Failed to load purchases.");
        setOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleExport = async () => {
    setLoading(true);

    try {
      const queryString = buildQueryString(getCurrentFilters(1), true);
      const res = await axios.get(
        `/portal/tm/loyalty/promotions/${ref}/purchases.json?${queryString}`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      );

      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", `loyalty_purchases_${ref}.xlsx`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrMsg("Failed to export purchases.");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpen(false)} severity="error" sx={{ width: "100%" }}>
          {errMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={1} mb={5}>
        {loaded && (
          <Card>
            <MDBox
              p={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <MDTypography variant="h6">Loyalty Purchases</MDTypography>

              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  onClick={() => navigate(`/tm/loyalty/promotions/${ref}`)}
                  startIcon={<Icon>west</Icon>}
                >
                  Back
                </MDButton>

                <MDButton
                  variant="contained"
                  color="dark"
                  onClick={handleExport}
                  startIcon={<Icon>download</Icon>}
                  size="small"
                >
                  Export Excel
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox px={2} pb={2}>
              <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center">
                <MDBox flex="1 1 240px">
                  <TextField
                    fullWidth
                    label="Search"
                    placeholder="Consumer, product, phone, merchant..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleSearch();
                    }}
                  />
                </MDBox>

                <MDBox flex="1 1 150px">
                  <MDInput
                    labelId="verified-filter-label"
                    value={verified}
                    label="Verification"
                    onChange={(event) => setVerified(event.target.value)}
                    SelectProps={{
                      style: {
                        padding: "12px 0px",
                      },
                    }}
                    select
                    fullWidth
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Verified</MenuItem>
                    <MenuItem value="false">Not Verified</MenuItem>
                  </MDInput>
                </MDBox>

                <MDBox flex="1 1 200px">
                  <MDInput
                    labelId="product-filter-label"
                    value={selectedProducts}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSelectedProducts(typeof value === "string" ? value.split(",") : value);
                    }}
                    input={<OutlinedInput label="Products" />}
                    renderValue={(selected) =>
                      products
                        .filter((product) => selected.includes(product.reference))
                        .map((product) => product.title)
                        .join(", ")
                    }
                    SelectProps={{
                      style: {
                        padding: "12px 0px",
                      },
                      multiple: true,
                    }}
                    select
                    fullWidth
                  >
                    {products.map((product) => (
                      <MenuItem key={product.reference} value={product.reference}>
                        <Checkbox checked={selectedProducts.includes(product.reference)} />
                        <ListItemText primary={product.title} />
                      </MenuItem>
                    ))}
                  </MDInput>
                </MDBox>

                <MDBox flex="1 1 200px">
                  <MDInput
                    labelId="territory-filter-label"
                    value={selectedTerritories}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSelectedTerritories(typeof value === "string" ? value.split(",") : value);
                    }}
                    input={<OutlinedInput label="Territories" />}
                    renderValue={(selected) =>
                      territories
                        .filter((territory) => selected.includes(territory.reference))
                        .map((territory) => territory.title)
                        .join(", ")
                    }
                    SelectProps={{
                      style: {
                        padding: "12px 0px",
                      },
                      multiple: true,
                    }}
                    select
                    fullWidth
                  >
                    {territories.map((territory) => (
                      <MenuItem key={territory.reference} value={territory.reference}>
                        <Checkbox checked={selectedTerritories.includes(territory.reference)} />
                        <ListItemText primary={territory.title} />
                      </MenuItem>
                    ))}
                  </MDInput>
                </MDBox>

                <MDBox flex="1 1 160px">
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </MDBox>

                <MDBox flex="1 1 160px">
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </MDBox>
              </MDBox>

              <MDBox mt={2} display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  onClick={handleReset}
                  startIcon={<Icon>restart_alt</Icon>}
                >
                  Reset
                </MDButton>

                <MDButton
                  variant="contained"
                  color="dark"
                  size="small"
                  onClick={handleSearch}
                  startIcon={<Icon>search</Icon>}
                >
                  Apply Filters
                </MDButton>
              </MDBox>
            </MDBox>

            <DataTable
              table={{
                columns: [
                  { Header: "Consumer", accessor: "consumer" },
                  { Header: "Product", accessor: "product" },
                  { Header: "Points", accessor: "points" },
                  { Header: "Total Price", accessor: "total" },
                  { Header: "Verified", accessor: "verified" },
                  { Header: "Created", accessor: "created" },
                  { Header: "Actions", accessor: "actions", align: "right" },
                ],
                rows: purchases,
              }}
              canSearch={false}
              isSorted
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
            />

            <MDBox
              px={2}
              py={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <MDTypography variant="button" color="text">
                Showing {purchases.length} of {pagination.total_records} purchases
              </MDTypography>

              <MDBox display="flex" alignItems="center" gap={1}>
                <TextField
                  select
                  size="small"
                  label="Rows"
                  value={pagination.page_size}
                  onChange={handlePageSizeChange}
                  sx={{ width: 100 }}
                >
                  {[25, 50, 100, 250, 500].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </TextField>

                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  disabled={!pagination.has_previous || loading}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  startIcon={<Icon>chevron_left</Icon>}
                >
                  Previous
                </MDButton>

                <MDTypography variant="button" color="text">
                  Page {pagination.page} of {pagination.total_pages}
                </MDTypography>

                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  disabled={!pagination.has_next || loading}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  endIcon={<Icon>chevron_right</Icon>}
                >
                  Next
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
